/**
 * Runtime Configuration Loader
 *
 * Loads config.json from the static directory at runtime.
 * This allows for environment-specific configuration without rebuilding.
 */

import type { AppConfig } from '$shared/types/api';

let cachedConfig: AppConfig | null = null;
let configPromise: Promise<AppConfig> | null = null;

/**
 * Loads configuration from /config.json
 * Results are cached to avoid multiple fetch calls
 */
export async function loadConfig(): Promise<AppConfig> {
  // Return cached config if available
  if (cachedConfig) {
    return cachedConfig;
  }

  // Return existing promise if already loading
  if (configPromise) {
    return configPromise;
  }

  // Fetch config from static directory
  configPromise = fetch('/config.json')
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Failed to load config: ${response.status} ${response.statusText}`);
      }
      return response.json();
    })
    .then((config: AppConfig) => {
      // Validate required fields
      if (!config.api?.host) {
        throw new Error('Config validation failed: api.host is required');
      }

      if (!config.auth?.aws_region || !config.auth?.user_pool_id || !config.auth?.user_pool_client_id) {
        throw new Error('Config validation failed: auth configuration is incomplete');
      }

      cachedConfig = config;
      return config;
    })
    .catch((error) => {
      configPromise = null; // Reset promise on error to allow retry
      throw error;
    });

  return configPromise;
}

/**
 * Gets cached config synchronously
 * Throws if config hasn't been loaded yet
 */
export function getConfig(): AppConfig {
  if (!cachedConfig) {
    throw new Error('Config not loaded. Call loadConfig() first.');
  }
  return cachedConfig;
}

/**
 * Gets cached config synchronously, returns null if not loaded
 */
export function getConfigOrNull(): AppConfig | null {
  return cachedConfig;
}

/**
 * Clears cached config (useful for testing)
 */
export function clearConfigCache(): void {
  cachedConfig = null;
  configPromise = null;
}

/**
 * Checks if config has been loaded
 */
export function isConfigLoaded(): boolean {
  return cachedConfig !== null;
}
